import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo, useState } from "react";
import {
  ButtonSize,
  Modal,
  Button,
  ButtonVariant,
  Checkbox,
  IconButton
} from "shared/components";
import { Card } from "shared/components/Card";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { ApplicantStatus, ApplicantType, Application } from "shared/types";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { XClose } from "untitledui-js-base";
import { cx } from "shared/utils";
import { Table } from "shared/app/Table";
import moment from "moment";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { formatToCurrency } from "utils/currency";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { ApplicationStatusBadge } from "../ApplicationStatusBadge";
import { Template } from "types/landlord/verificationTemplate";
import { Money } from "types/landlord/reports";
import { getFullName } from "shared/utils/get-full-name";

type ApplicationWithInvoice = Application & {
  refundable_invoice: {
    created_at: string;
    due_date?: string;
    id: number;
    paid_at?: string;
    period?: string;
    status: "fulfilled" | string;
    total: Money;
    updated_at?: string;
  };
};

const getApplication = async (id: string) => {
  return instance(`/portal/v1/applications/${id}`).then(
    ({ data }) =>
      data as ApplicationWithInvoice & { verification_template: Template }
  );
};

enum View {
  Cancel = "Cancel",
  ConfirmRefund = "ConfirmRefund"
}

export const CancelOrRefundModal = ({
  onClose,
  applicationId,
  onSuccess,
  applicantId
}: {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
}) => {
  const navigate = useNavigate();
  const [refund, setRefund] = useState(false);
  const [view, setView] = useState<View>(View.Cancel);

  const applicationMeta = useQuery({
    queryKey: ["application", String(applicationId)],
    queryFn: () => getApplication(applicationId),
    enabled: Boolean(applicationId)
  });

  const allUsers = [
    ...(applicationMeta.data?.applicants || []),
    ...(applicationMeta.data?.pending_magic_links || [])
  ];

  const applicant = allUsers.find(
    (applicant) => applicantId && String(applicant.id) === String(applicantId)
  );

  const hasSubmittedApplications = applicationMeta.data?.applicants.some(
    (applicant) => applicant.application_status === ApplicantStatus.Submitted
  );

  const tableData = useMemo(() => {
    return (applicant ? [applicant] : allUsers).map((applicant) => ({
      ...applicant,
      paidBy:
        (applicant.role === ApplicantType.CoSigner &&
          applicationMeta.data?.verification_template.co_signer_fee.cents ===
            0) ||
        (applicant.role === ApplicantType.Applicant &&
          applicationMeta.data?.verification_template.applicant_fee.cents === 0)
          ? "Partner"
          : applicant.role === ApplicantType.Applicant
            ? "Applicant"
            : "Guarantor"
    }));
  }, [applicationMeta.data?.applicants, applicantId, applicant]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!applicantId && applicationId)
        return instance.post(
          `/portal/v1/applications/${applicationId}/cancel`,
          {
            refund
          }
        );

      if (!("active" in (applicant || {})))
        return instance.post(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/cancel`,
          {
            refund
          }
        );

      return instance.post(`/portal/v1/magic_links/${applicant?.id}/cancel`);
    },
    mutationKey: ["cancel", "application", String(applicationId)],
    onSuccess: () => {
      toast.success(`Application successfully canceled`);
      if (onSuccess) onSuccess();
      else navigate(`/applications/${applicationId}`);

      onClose();
    },
    onError: (error) => {
      toast.error(
        `Error ${refund ? "refunding" : "cancelling"} applications: ${getErrorMessage(error)}`
      );
    }
  });

  const columns = useMemo(() => {
    return [
      {
        accessorFn: (data) =>
          getFullName({
            firstName: data.first_name,
            lastName: data.last_name,
            middleName: data.middle_name,
            short: true
          }),
        header: "Applicant",
        cell: (data) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 120,
        cell: (data) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("ll")
              : "--"}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.total",
        header: "Amount",
        size: 120,
        cell: (data) => (
          <p className="text-sm text-content-400">
            {data.getValue() ? formatToCurrency(data.getValue()) : "--"}
          </p>
        )
      },
      {
        accessorKey: "paidBy",
        header: "Paid by",
        size: 120,
        cell: (data) => (
          <p className="text-sm text-content-400">{data.getValue() || "--"}</p>
        )
      }
    ];
  }, []);

  const table = useReactTable({
    // remove
    data: tableData.filter((td) => "first_name" in td),
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName="max-w-[600px] max-h-[640px] w-full min-h-[300px] flex flex-col h-full"
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">
          {view === View.Cancel
            ? "Confirm application cancellation"
            : "Confirm refund"}
        </h1>
        <IconButton
          onClick={onClose}
          borderLess
          className="ml-auto translate-x-2"
          Icon={XClose}
        />
      </div>
      <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
        <InfoIcon className="text-warning-500 h-5 w-5" />
        <p className="text-sm text-warning-700 font-medium">
          {view === View.Cancel
            ? "Applicants will no longer be able to access this application. Canceling applications can't be undone."
            : "You cannot undo this action. Once a refund is issued it can take up to 5-7 business days"}
        </p>
      </div>

      <div className="h-full grow-1 overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              {view === View.Cancel
                ? "The following application will be canceled"
                : "The following items will be refunded"}
            </p>
            {view === View.Cancel ? (
              <Card
                className={cx("px-6 ", tableData.length > 1 ? "py-4" : "py-0")}
              >
                {tableData.length > 1 ? (
                  <div className="flex gap-2 items-center justify-between">
                    <div className="max-w-[380px]">
                      <p className="text-sm text-content-400 mb-2">
                        Application group
                      </p>
                      <h3 className="truncate text-base text-content-1000 font-bold pb-4 border-b border-alpha-40">
                        {tableData
                          .map((applicant) =>
                            "first_name" in applicant &&
                            applicant.first_name &&
                            applicant.last_name
                              ? getFullName({
                                  firstName: applicant.first_name,
                                  lastName: applicant.last_name,
                                  middleName: applicant.middle_name,
                                  short: true
                                })
                              : applicant.email
                          )
                          .join(", ")}
                      </h3>
                    </div>

                    <ApplicationStatusBadge
                      className="shrink-0"
                      type="application"
                      status={applicationMeta.data?.status}
                    />
                  </div>
                ) : null}

                <ul className="mt-4">
                  {tableData.map((applicant) => (
                    <li key={applicant.email} className="mb-4">
                      <p className="text-sm text-content-1000 font-medium mb-2">
                        {"first_name" in applicant &&
                        applicant.first_name &&
                        applicant.last_name
                          ? getFullName({
                              firstName: applicant.first_name,
                              lastName: applicant.last_name,
                              middleName: applicant.middle_name,
                              short: true
                            })
                          : applicant.email}
                      </p>
                      <p className="text-xs text-content-400">
                        {applicant.role === ApplicantType.CoSigner
                          ? "Guarantor"
                          : "Applicant"}
                      </p>
                    </li>
                  ))}
                </ul>

                {hasSubmittedApplications && (
                  <div className="pt-4 pb-4 border-t border-alpha-100">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={refund}
                        onChange={(e) => setRefund(e.target.checked)}
                        label="Request refund"
                      />
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              <Table table={table} />
            )}
          </>
        )}
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button
          size={ButtonSize.SM}
          onClick={() => {
            if (view === View.ConfirmRefund) setView(View.Cancel);
            else onClose();
          }}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            if (refund && view === View.Cancel) setView(View.ConfirmRefund);
            else if (!isPending) mutate();
          }}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending}
        >
          {refund && view === View.Cancel
            ? "Refund and cancel"
            : "Cancel application"}
        </Button>
      </div>
    </Modal>
  );
};
